import { useMemo } from "react"
import { CellStatus, UserStatus } from "."
import { IconColor } from "@/components/shared/Icon"
import { TypefaceVariant } from "@/types/alert"
import { Cell } from "@/types/cell"
import { TypefaceColors } from "@/types/index"

interface UseCellCardStateProps {
   cellStatus: Cell["data"]["status"]
   occupied: boolean
   maintenance: boolean
   typefaceColor?: keyof typeof TypefaceVariant
   userStatus?: UserStatus
}

export const useCellCardState = ({
   cellStatus: cell,
   occupied,
   maintenance,
   typefaceColor,
   userStatus,
}: UseCellCardStateProps) => {
   const cellStatus = useMemo(() => {
      if (maintenance) {
         return CellStatus.MAINTENANCE
      }
      if (!cell.sensorConnected) {
         return CellStatus.DISCONNECTED
      }
      if (occupied) {
         return CellStatus.OCCUPIED
      }
      return CellStatus.VACANT
   }, [cell.sensorConnected, maintenance, occupied])

   const hideVitals = useMemo(() => {
      if (!cell.sensorConnected) {
         return true
      }
      if ([CellStatus.VACANT, CellStatus.MAINTENANCE].includes(cellStatus)) {
         return true
      }
      return false
   }, [cell.sensorConnected, cellStatus])

   const noVitals = useMemo(
      () =>
         userStatus !== UserStatus.MOVING &&
         cellStatus === CellStatus.OCCUPIED &&
         !cell.humanPresence,
      [userStatus, cellStatus, cell.humanPresence]
   )

   const humanPresenceInVacant = useMemo(
      () => cellStatus === CellStatus.VACANT && cell.humanPresence,
      [cellStatus, cell.humanPresence]
   )

   const isMoving = useMemo(
      () => userStatus === UserStatus.MOVING,
      [userStatus]
   )

   const typefaceClassName = useMemo(() => {
      if (cellStatus !== CellStatus.OCCUPIED || !typefaceColor) {
         return ""
      }
      return `cell-card__typeface--${TypefaceVariant[typefaceColor]}`
   }, [cellStatus, typefaceColor])

   const typefaceIconColor = useMemo(() => {
      if (
         [
            CellStatus.INMATE_TRANSFER,
            CellStatus.VACANT,
            CellStatus.MAINTENANCE,
         ].includes(cellStatus)
      ) {
         return IconColor.GUNMETAL
      }
      switch (typefaceColor) {
         case TypefaceColors.WHITE:
            return IconColor.WHITE
         case TypefaceColors.GUNMETAL:
            return IconColor.GUNMETAL
         default:
            return IconColor.WHITE
      }
   }, [cellStatus, typefaceColor])

   const description = useMemo(() => {
      switch (cellStatus) {
         case CellStatus.MAINTENANCE:
            return "Maintenance"
         case CellStatus.DISCONNECTED:
            return "Disconnected"
         case CellStatus.VACANT:
            return "Vacant"
         default:
            return ""
      }
   }, [cellStatus])

   return {
      cellStatus,
      typefaceClassName,
      typefaceIconColor,
      description,
      hideVitals,
      noVitals,
      humanPresenceInVacant,
      isMoving,
   }
}
